import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    fetchProductCategories,
    createProductCategory,
    updateProductCategory,
    deleteProductCategory,
    clearProductCategoryState,
} from "../redux/slices/productCategorySlice";
import { FaEdit, FaTrash } from "react-icons/fa";
import { toast } from "react-toastify";

const ManageProductCategory = () => {
    const dispatch = useDispatch();

    const {
        categories,
        loading,
        successMessage,
        error,
    } = useSelector((state) => state.productCategory);

    const [show, setShow] = useState(false);
    const [editId, setEditId] = useState(null);
    const [formData, setFormData] = useState({
        name: "",
        parent: "",
    });

    /* ===============================
       FETCH CATEGORIES
    ================================ */
    useEffect(() => {
        dispatch(fetchProductCategories());
    }, [dispatch]);

    /* ===============================
       SUCCESS / ERROR HANDLING
    ================================ */
    useEffect(() => {
        if (successMessage) {
            toast.success(successMessage);
            dispatch(clearProductCategoryState());
            setShow(false);
            setEditId(null);
            setFormData({ name: "", parent: "" });
        }

        if (error) {
            toast.error(error);
            dispatch(clearProductCategoryState());
        }
    }, [successMessage, error, dispatch]);

    /* ===============================
       SUBMIT
    ================================ */
    const handleSubmit = () => {
        if (!formData.name.trim()) {
            toast.error("Category name is required");
            return;
        }

        if (editId) {
            dispatch(
                updateProductCategory({
                    id: editId,
                    data: formData,
                })
            );
        } else {
            dispatch(createProductCategory(formData));
        }
    };

    /* ===============================
       EDIT
    ================================ */
    const handleEdit = (cat) => {
        setEditId(cat._id);
        setFormData({
            name: cat.name,
            parent: cat.parent?._id || "",
        });
        setShow(true);
    };

    /* ===============================
       DELETE
    ================================ */
    const handleDelete = (id) => {
        if (window.confirm("Are you sure you want to delete this category?")) {
            dispatch(deleteProductCategory(id));
        }
    };

    /* ===============================
       FILTER CATEGORIES
    ================================ */
    const mainCategories = categories.filter((c) => !c.parent);
    const subCategories = categories.filter((c) => c.parent);

    return (
        <div className="container mt-4">
            <div className="card">
                {/* HEADER */}
                <div className="card-header d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">Manage Product Categories</h6>
                    <button
                        className="btn btn-primary btn-sm"
                        onClick={() => setShow(true)}
                    >
                        + Add Category
                    </button>
                </div>

                {/* BODY */}
                <div className="card-body">
                    {loading ? (
                        <div className="text-center py-4">
                            <div className="spinner-border spinner-border-sm" />
                        </div>
                    ) : (
                        <>
                            {/* ================= MAIN CATEGORIES ================= */}
                            <h6 className="mb-2">Main Categories</h6>
                            <div className="table-responsive mb-4">
                                <table className="table table-sm table-bordered table-hover">
                                    <thead className="table-dark">
                                        <tr>
                                            <th>Name</th>
                                            <th style={{ width: "120px" }}>
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {mainCategories.length ? (
                                            mainCategories.map((cat) => (
                                                <tr key={cat._id}>
                                                    <td>{cat.name}</td>
                                                    <td>
                                                        <button
                                                            className="btn btn-warning btn-sm me-2"
                                                            onClick={() =>
                                                                handleEdit(cat)
                                                            }
                                                        >
                                                            <FaEdit />
                                                        </button>
                                                        <button
                                                            className="btn btn-danger btn-sm"
                                                            onClick={() =>
                                                                handleDelete(
                                                                    cat._id
                                                                )
                                                            }
                                                        >
                                                            <FaTrash />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td
                                                    colSpan="2"
                                                    className="text-center"
                                                >
                                                    No main categories found
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* ================= SUB CATEGORIES ================= */}
                            <h6 className="mb-2">Sub Categories</h6>
                            <div className="table-responsive">
                                <table className="table table-sm table-bordered table-hover">
                                    <thead className="table-dark">
                                        <tr>
                                            <th>Name</th>
                                            <th>Parent Category</th>
                                            <th style={{ width: "120px" }}>
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {subCategories.length ? (
                                            subCategories.map((cat) => (
                                                <tr key={cat._id}>
                                                    <td>{cat.name}</td>
                                                    <td>
                                                        {cat.parent?.name}
                                                    </td>
                                                    <td>
                                                        <button
                                                            className="btn btn-warning btn-sm me-2"
                                                            onClick={() =>
                                                                handleEdit(cat)
                                                            }
                                                        >
                                                            <FaEdit />
                                                        </button>
                                                        <button
                                                            className="btn btn-danger btn-sm"
                                                            onClick={() =>
                                                                handleDelete(
                                                                    cat._id
                                                                )
                                                            }
                                                        >
                                                            <FaTrash />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td
                                                    colSpan="3"
                                                    className="text-center"
                                                >
                                                    No sub categories found
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* ================= MODAL ================= */}
            {show && (
                <>
                    <div className="modal fade show d-block" tabIndex="-1">
                        <div className="modal-dialog">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h6 className="modal-title">
                                        {editId
                                            ? "Edit Category"
                                            : "Add Category"}
                                    </h6>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => setShow(false)}
                                    />
                                </div>

                                <div className="modal-body">
                                    <div className="mb-2">
                                        <label className="form-label">
                                            Category Name
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            value={formData.name}
                                            onChange={(e) =>
                                                setFormData({
                                                    ...formData,
                                                    name: e.target.value,
                                                })
                                            }
                                        />
                                    </div>

                                    <div className="mb-2">
                                        <label className="form-label">
                                            Parent Category
                                        </label>
                                        <select
                                            className="form-select form-select-sm"
                                            value={formData.parent}
                                            onChange={(e) =>
                                                setFormData({
                                                    ...formData,
                                                    parent: e.target.value,
                                                })
                                            }
                                        >
                                            <option value="">
                                                Main Category
                                            </option>
                                            {mainCategories.map((cat) => (
                                                <option
                                                    key={cat._id}
                                                    value={cat._id}
                                                >
                                                    {cat.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="modal-footer">
                                    <button
                                        className="btn btn-secondary btn-sm"
                                        onClick={() => setShow(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className="btn btn-primary btn-sm"
                                        onClick={handleSubmit}
                                    >
                                        Save
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="modal-backdrop fade show"></div>
                </>
            )}
        </div>
    );
};

export default ManageProductCategory;
